import { Calendar, CalendarPermissionAction, EntityId, Group, GroupRole, Todo, User, UserRole, Event as OwnitEvent } from './models';

export type AppTabId = 'calendar' | 'todo' | 'share' | 'decorate' | 'settings';

export type AppPermissionAction =
    | CalendarPermissionAction
    | 'access_calendar'
    | 'access_todo'
    | 'access_share'
    | 'access_decorate'
    | 'access_settings'
    | 'manage_personal_calendar'
    | 'manage_todo'
    | 'manage_decoration'
    | 'manage_settings'
    | 'save_daily_receipt';

export interface PermissionContext {
    group?: Group | null;
    calendar?: Calendar | null;
    ownerId?: EntityId | null;
    authorId?: EntityId | null;
    assigneeId?: EntityId | null;
}

const ALL_PERMISSIONS: AppPermissionAction[] = [
    'access_calendar',
    'access_todo',
    'access_share',
    'access_decorate',
    'access_settings',
    'manage_personal_calendar',
    'manage_todo',
    'manage_decoration',
    'manage_settings',
    'save_daily_receipt',
    'manage_group',
    'manage_shared_calendar',
    'create_shared_event',
    'edit_own_shared_event',
    'view_shared_event'
];

const ROLE_PERMISSIONS: Record<UserRole, AppPermissionAction[]> = {
    owner: ALL_PERMISSIONS,
    admin: ALL_PERMISSIONS,
    member: [
        'access_calendar',
        'access_todo',
        'access_share',
        'access_decorate',
        'access_settings',
        'manage_personal_calendar',
        'manage_todo',
        'manage_decoration',
        'manage_settings',
        'save_daily_receipt',
        'create_shared_event',
        'edit_own_shared_event',
        'view_shared_event'
    ],
    viewer: [
        'access_calendar',
        'access_todo',
        'access_share',
        'access_decorate',
        'access_settings',
        'view_shared_event'
    ]
};

const GROUP_ROLE_PERMISSIONS: Record<GroupRole, CalendarPermissionAction[]> = {
    owner: ['manage_group', 'manage_shared_calendar', 'create_shared_event', 'edit_own_shared_event', 'view_shared_event'],
    admin: ['manage_group', 'manage_shared_calendar', 'create_shared_event', 'edit_own_shared_event', 'view_shared_event'],
    member: ['create_shared_event', 'edit_own_shared_event', 'view_shared_event'],
    viewer: ['view_shared_event']
};

const TAB_PERMISSIONS: Record<AppTabId, AppPermissionAction> = {
    calendar: 'access_calendar',
    todo: 'access_todo',
    share: 'access_share',
    decorate: 'access_decorate',
    settings: 'access_settings'
};

export function isAdminUser(user: (Pick<User, 'id' | 'username' | 'role'> & Partial<Pick<User, 'email'>>) | null | undefined): boolean {
    if (!user) return false;
    return normalizeRole(user.role) === 'admin';
}

export function hasPermission(user: User | null | undefined, permission: AppPermissionAction, context: PermissionContext = {}): boolean {
    if (!user || !user.id) return false;
    if (isAdminUser(user)) return true;

    const role = normalizeRole(user.role);
    if (permission === 'manage_personal_calendar' && context.ownerId === user.id) return true;
    if (permission === 'manage_todo' && isOwnRecord(user.id, context)) return true;
    if (permission === 'manage_decoration' && (!context.ownerId || context.ownerId === user.id)) return true;
    if (permission === 'manage_settings' && (!context.ownerId || context.ownerId === user.id)) return true;

    if (isCalendarPermission(permission)) {
        const groupRole = getGroupRole(user.id, context.group);
        if (!groupRole) return false;
        if (permission === 'edit_own_shared_event' && !isPrivilegedRole(groupRole)) {
            return roleHasCalendarPermission(groupRole, permission) && isOwnRecord(user.id, context);
        }
        return roleHasCalendarPermission(groupRole, permission);
    }

    return ROLE_PERMISSIONS[role].includes(permission);
}

export function canAccessTab(user: User | null | undefined, tab: AppTabId): boolean {
    return hasPermission(user, TAB_PERMISSIONS[tab]);
}

export function canManageEvent(user: User | null | undefined, event: OwnitEvent | null, calendar: Calendar | null | undefined, context: PermissionContext = {}): boolean {
    if (!calendar) return isAdminUser(user);
    if (!user || !user.id) return false;
    if (calendar.readOnly || calendar.externalId || event?.readOnly || event?.externalId) return false;
    if (isAdminUser(user)) return true;

    if (calendar.type === 'shared') {
        if (!event) return hasPermission(user, 'create_shared_event', { ...context, calendar });
        const sharedContext = { ...context, calendar, authorId: event.authorId, assigneeId: event.assigneeId };
        return hasPermission(user, 'manage_shared_calendar', sharedContext) || hasPermission(user, 'edit_own_shared_event', sharedContext);
    }

    if (calendar.ownerId !== user.id) return false;
    if (event && event.authorId !== user.id) return false;
    return hasPermission(user, 'manage_personal_calendar', { ...context, calendar, ownerId: calendar.ownerId });
}

export function canManageTodo(user: User | null | undefined, todo: Todo | null, context: PermissionContext = {}): boolean {
    if (!todo) return hasPermission(user, 'manage_todo', context);
    return hasPermission(user, 'manage_todo', {
        ...context,
        authorId: todo.authorId,
        assigneeId: todo.assigneeId
    });
}

export function canManageDecoration(user: User | null | undefined, ownerId: EntityId | null | undefined): boolean {
    return hasPermission(user, 'manage_decoration', { ownerId });
}

export function canManageSettings(user: User | null | undefined, ownerId: EntityId | null | undefined): boolean {
    return hasPermission(user, 'manage_settings', { ownerId });
}

export function canManageGroup(user: User | null | undefined, group: Group | null | undefined): boolean {
    return hasPermission(user, 'manage_group', { group });
}

export function canSaveDailyReceipt(user: User | null | undefined, ownerId: EntityId | null | undefined): boolean {
    return hasPermission(user, 'save_daily_receipt', { ownerId });
}

export function canViewGroup(user: User | null | undefined, group: Group | null | undefined): boolean {
    if (!user || !user.id || !group?.id) return false;
    if (isAdminUser(user)) return true;
    return Boolean(getGroupRole(user.id, group));
}

export function canViewCalendar(user: User | null | undefined, calendar: Calendar | null | undefined, group: Group | null | undefined = null): boolean {
    if (!user || !user.id || !calendar) return false;
    if (isAdminUser(user)) return true;
    if (calendar.type === 'personal') return calendar.ownerId === user.id;
    if (!calendar.groupId || !group?.id || calendar.groupId !== group.id) return false;
    return hasPermission(user, 'view_shared_event', { group, calendar });
}

export function canViewEvent(user: User | null | undefined, event: OwnitEvent | null | undefined, calendar: Calendar | null | undefined, group: Group | null | undefined = null): boolean {
    if (!event || !calendar) return false;
    if (!canViewCalendar(user, calendar, group)) return false;
    if (!user || !user.id) return false;
    if (isAdminUser(user)) return true;
    if (calendar.type === 'personal') return calendar.ownerId === user.id && event.authorId === user.id;
    return Boolean(calendar.groupId && group?.id === calendar.groupId && hasPermission(user, 'view_shared_event', { group, calendar, authorId: event.authorId, assigneeId: event.assigneeId }));
}

export function userRoleLabel(role: UserRole | null | undefined): string {
    const labels: Record<UserRole, string> = {
        owner: '소유자',
        admin: '관리자',
        member: '멤버',
        viewer: '읽기 전용'
    };
    return labels[normalizeRole(role)];
}

export function getGroupRole(userId: EntityId, group: Group | null | undefined): GroupRole | null {
    return group?.members.find((member) => member.userId === userId && member.status === 'active')?.role || null;
}

function roleHasCalendarPermission(role: GroupRole | UserRole, permission: CalendarPermissionAction): boolean {
    return GROUP_ROLE_PERMISSIONS[normalizeRole(role)].includes(permission);
}

function isCalendarPermission(permission: AppPermissionAction): permission is CalendarPermissionAction {
    return permission === 'manage_group'
        || permission === 'manage_shared_calendar'
        || permission === 'create_shared_event'
        || permission === 'edit_own_shared_event'
        || permission === 'view_shared_event';
}

function isPrivilegedRole(role: GroupRole | UserRole): boolean {
    const normalized = normalizeRole(role);
    return normalized === 'owner' || normalized === 'admin';
}

function isOwnRecord(userId: EntityId, context: PermissionContext): boolean {
    return context.ownerId === userId || context.authorId === userId || context.assigneeId === userId;
}

function normalizeRole(role: UserRole | GroupRole | null | undefined): UserRole {
    if (role === 'admin' || role === 'member' || role === 'viewer') return role;
    return 'owner';
}
